import React, { useState, useEffect } from 'react';
import { noteService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Check, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const QuickNotes = ({ title = "Notas Rápidas" }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      const data = await noteService.getAll();
      setNotes(data);
    } catch (error) {
      console.error('Error loading notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await noteService.create({ content: newNote });
      setNewNote('');
      loadNotes();
      toast.success('Nota creada');
    } catch (error) {
      toast.error('Error al crear nota');
    }
  };

  const handleToggle = async (note) => {
    try {
      await noteService.update(note.id, { is_completed: !note.is_completed });
      loadNotes();
    } catch (error) {
      toast.error('Error al actualizar nota');
    }
  };

  const handleDelete = async (noteId) => {
    try {
      await noteService.delete(noteId);
      loadNotes();
      toast.success('Nota eliminada');
    } catch (error) {
      toast.error('Error al eliminar nota');
    }
  };

  const activeNotes = notes.filter(n => !n.is_completed);
  const completedNotes = notes.filter(n => n.is_completed);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCreate} className="flex gap-2 mb-4">
          <Input
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Nueva nota..."
            className="flex-1"
          />
          <Button type="submit" size="sm">
            <Plus className="w-4 h-4" />
          </Button>
        </form>

        {loading ? (
          <div className="text-sm text-slate-500">Cargando...</div>
        ) : (
          <div className="space-y-4">
            {/* Notas activas */}
            <div className="space-y-2">
              {activeNotes.length === 0 && completedNotes.length === 0 ? (
                <p className="text-sm text-slate-500">No hay notas</p>
              ) : (
                activeNotes.map(note => (
                  <div key={note.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 group">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 mt-0.5"
                      onClick={() => handleToggle(note)}
                    >
                      <div className="w-4 h-4 rounded border-2 border-slate-300 hover:border-green-500" />
                    </Button>
                    <p className="flex-1 text-sm text-slate-700">{note.content}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDelete(note.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))
              )}
            </div>

            {/* Notas completadas */}
            {completedNotes.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-xs text-slate-500 mb-2 uppercase">Completadas</p>
                <div className="space-y-2">
                  {completedNotes.map(note => (
                    <div key={note.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 group">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 mt-0.5"
                        onClick={() => handleToggle(note)}
                      >
                        <Check className="w-4 h-4 text-green-500" />
                      </Button>
                      <p className="flex-1 text-sm text-slate-400 line-through">{note.content}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(note.id)}
                      >
                        <X className="w-4 h-4 text-slate-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuickNotes;